import { useEffect, useMemo, useState } from "react";
import {
  X,
  AlertCircle,
  Plus,
  GitBranch,
  BookOpen,
  Pencil,
  Trash2,
  MessageSquare,
} from "lucide-react";
import { supabase } from "../lib/supabase.js";

// Mirrors the categories/statuses/priorities used by the in-engagement
// FeedbackModal in RdStudyTool.jsx so both views read the same shape.

const CATEGORIES = [
  { id: "bug",         label: "Bug / Error",          icon: AlertCircle, dot: "bg-rose-500" },
  { id: "enhancement", label: "Enhancement",          icon: Plus,        dot: "bg-sky-500" },
  { id: "workflow",    label: "Workflow",             icon: GitBranch,   dot: "bg-amber-500" },
  { id: "content",     label: "Content / Citation",   icon: BookOpen,    dot: "bg-stone-500" },
  { id: "design",      label: "Design / UX",          icon: Pencil,      dot: "bg-violet-500" },
];

const STATUSES = [
  { id: "open",      label: "Open",      pill: "bg-amber-100 text-amber-800 border-amber-300" },
  { id: "reviewing", label: "Reviewing", pill: "bg-sky-100 text-sky-800 border-sky-300" },
  { id: "planned",   label: "Planned",   pill: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { id: "done",      label: "Done",      pill: "bg-stone-200 text-stone-600 border-stone-300" },
  { id: "wontdo",    label: "Won't Do",  pill: "bg-stone-100 text-stone-500 border-stone-300" },
];

const PRIORITIES = [
  { id: "low",  label: "Low",    color: "text-stone-500" },
  { id: "med",  label: "Medium", color: "text-amber-700" },
  { id: "high", label: "High",   color: "text-rose-700" },
];

function categoryMeta(id) {
  return CATEGORIES.find((c) => c.id === id) || CATEGORIES[1];
}
function statusMeta(id) {
  return STATUSES.find((s) => s.id === id) || STATUSES[0];
}
function priorityMeta(id) {
  return PRIORITIES.find((p) => p.id === id) || PRIORITIES[1];
}

function fmtRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function FeedbackPanel({ profile, onClose }) {
  const [rows, setRows] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [showNewForm, setShowNewForm] = useState(false);
  const [draft, setDraft] = useState({
    title: "",
    description: "",
    category: "enhancement",
    priority: "med",
  });

  const refresh = async () => {
    setError(null);
    const [{ data: fb, error: e1 }, { data: ps }] = await Promise.all([
      supabase
        .from("feedback")
        .select("id, content, author_id, created_at")
        .order("created_at", { ascending: false }),
      supabase.from("user_profiles").select("id, email, display_name"),
    ]);
    if (e1) {
      setError(e1.message);
      setLoading(false);
      return;
    }
    setRows(fb || []);
    const byId = {};
    for (const p of ps || []) byId[p.id] = p;
    setProfiles(byId);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    const channel = supabase
      .channel("feedback-panel")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "feedback" },
        () => refresh(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const c = r.content || {};
      const status = c.status || "open";
      const category = c.category || "enhancement";
      if (filterStatus !== "all" && status !== filterStatus) return false;
      if (filterCategory !== "all" && category !== filterCategory) return false;
      return true;
    });
  }, [rows, filterStatus, filterCategory]);

  const counts = useMemo(() => {
    const out = { all: rows.length };
    for (const s of STATUSES) out[s.id] = 0;
    for (const r of rows) {
      const status = r.content?.status || "open";
      out[status] = (out[status] || 0) + 1;
    }
    return out;
  }, [rows]);

  const updateStatus = async (id, status) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    const updated = {
      ...(row.content || {}),
      status,
      updatedAt: new Date().toISOString(),
    };
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, content: updated } : r)),
    );
    const { error } = await supabase
      .from("feedback")
      .update({ content: updated })
      .eq("id", id);
    if (error) {
      setError(error.message);
      refresh();
    }
  };

  const deleteEntry = async (id) => {
    if (!confirm("Delete this feedback item? This cannot be undone.")) return;
    setRows((prev) => prev.filter((r) => r.id !== id));
    const { error } = await supabase.from("feedback").delete().eq("id", id);
    if (error) {
      setError(error.message);
      refresh();
    }
  };

  const submitNew = async () => {
    if (!draft.title.trim()) return;
    const entry = {
      id: `fb_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      title: draft.title.trim(),
      description: draft.description.trim(),
      category: draft.category,
      priority: draft.priority,
      submitter: profile?.display_name || profile?.email || "",
      phase: "home",
      status: "open",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    const { error } = await supabase.from("feedback").insert({
      content: entry,
      author_id: profile?.id || null,
    });
    if (error) {
      setError(error.message);
      return;
    }
    setDraft({ title: "", description: "", category: "enhancement", priority: "med" });
    setShowNewForm(false);
    refresh();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 sans"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-[#FAFCFE] rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden border border-stone-200">
        {/* Header */}
        <div className="bg-stone-900 text-white px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-sky-500/20 flex items-center justify-center">
              <MessageSquare size={18} className="text-sky-300" />
            </div>
            <div>
              <h2 className="serif text-lg leading-tight">
                Prototype Feedback
              </h2>
              <p className="text-[11px] text-stone-400">
                Firm-wide pool · {counts.all} total · {counts.open || 0} open
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowNewForm((v) => !v)}
              className="px-3 py-1.5 text-xs rounded-md bg-sky-500 hover:bg-sky-400 text-white font-medium flex items-center gap-1.5"
            >
              <Plus size={14} strokeWidth={2.5} />
              New note
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-md hover:bg-white/10 flex items-center justify-center"
              title="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* New entry form */}
        {showNewForm && (
          <div className="bg-sky-50/60 border-b border-sky-100 px-6 py-4 shrink-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <input
                type="text"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="Short title (required)"
                className="px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:border-sky-500"
              />
              <div className="flex gap-2">
                <select
                  value={draft.category}
                  onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                  className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:border-sky-500"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
                <select
                  value={draft.priority}
                  onChange={(e) => setDraft({ ...draft, priority: e.target.value })}
                  className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:border-sky-500"
                >
                  {PRIORITIES.map((p) => (
                    <option key={p.id} value={p.id}>{p.label} priority</option>
                  ))}
                </select>
              </div>
            </div>
            <textarea
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="Description, repro steps, or context (optional)"
              rows={3}
              className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:border-sky-500"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                onClick={() => {
                  setShowNewForm(false);
                  setDraft({ title: "", description: "", category: "enhancement", priority: "med" });
                }}
                className="px-3 py-1.5 text-xs rounded-md border border-stone-300 hover:bg-stone-50 text-stone-700"
              >
                Cancel
              </button>
              <button
                onClick={submitNew}
                disabled={!draft.title.trim()}
                className="px-3 py-1.5 text-xs rounded-md bg-sky-600 hover:bg-sky-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save note
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="px-6 py-3 border-b border-stone-200 bg-white shrink-0 flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mr-1">
            Status
          </span>
          <FilterPill
            label={`All (${counts.all || 0})`}
            active={filterStatus === "all"}
            onClick={() => setFilterStatus("all")}
          />
          {STATUSES.map((s) => (
            <FilterPill
              key={s.id}
              label={`${s.label} (${counts[s.id] || 0})`}
              active={filterStatus === s.id}
              onClick={() => setFilterStatus(s.id)}
            />
          ))}
          <span className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold ml-4 mr-1">
            Category
          </span>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-2 py-1 border border-stone-300 rounded-md text-xs bg-white focus:outline-none focus:border-sky-500"
          >
            <option value="all">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && (
            <div className="mb-4 px-4 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm">
              {error}
            </div>
          )}
          {loading ? (
            <div className="text-center text-stone-500 text-sm py-12">
              Loading feedback…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-stone-500 text-sm py-12">
              {rows.length === 0
                ? "No feedback yet. Add the first note to start tracking improvements."
                : "No feedback matches the current filters."}
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((r) => (
                <FeedbackCard
                  key={r.id}
                  row={r}
                  author={profiles[r.author_id]}
                  onStatusChange={(s) => updateStatus(r.id, s)}
                  onDelete={() => deleteEntry(r.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterPill({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition ${
        active
          ? "bg-stone-900 text-white"
          : "bg-stone-100 text-stone-600 hover:bg-stone-200"
      }`}
    >
      {label}
    </button>
  );
}

function FeedbackCard({ row, author, onStatusChange, onDelete }) {
  const c = row.content || {};
  const cat = categoryMeta(c.category);
  const status = statusMeta(c.status || "open");
  const priority = priorityMeta(c.priority);
  const Icon = cat.icon;

  return (
    <div className="bg-white border border-stone-200 rounded-lg p-4 shadow-sm hover:shadow-md transition">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="mt-0.5 w-8 h-8 rounded-md bg-stone-100 flex items-center justify-center shrink-0">
            <Icon size={15} className="text-stone-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                {cat.label}
              </span>
              <span className={`text-[10px] uppercase font-bold ${priority.color}`}>
                · {priority.label} priority
              </span>
              {c.phase && c.phase !== "home" && (
                <span className="text-[10px] text-stone-400 mono">
                  · {c.phase}
                </span>
              )}
            </div>
            <div className="font-semibold text-stone-900 text-sm mb-1 break-words">
              {c.title || "(no title)"}
            </div>
            {c.description && (
              <div className="text-stone-700 text-xs leading-relaxed whitespace-pre-wrap mb-2">
                {c.description}
              </div>
            )}
            <div className="flex items-center gap-2 text-[10px] text-stone-500 mono">
              <span>
                {author?.display_name || author?.email || c.submitter || "Unknown"}
              </span>
              <span>·</span>
              <span>{fmtRelative(c.createdAt || row.created_at)}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2 shrink-0">
          <select
            value={c.status || "open"}
            onChange={(e) => onStatusChange(e.target.value)}
            className={`text-[11px] font-medium px-2 py-1 rounded-md border focus:outline-none cursor-pointer ${status.pill}`}
            title="Change status"
          >
            {STATUSES.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
          <button
            onClick={onDelete}
            className="w-7 h-7 rounded-md hover:bg-rose-50 text-stone-400 hover:text-rose-600 flex items-center justify-center"
            title="Delete"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}
